<template>
  <video ref="videoPlayer" class="video-js glass-effect max-ml:rounded-2 max-ml:overflow-hidden" controls preload="auto"
    v-bind="computedVideoAttrs" playsinline webkit-playsinline x5-video-player-type="h5"
    x5-video-player-fullscreen="false" x5-playsinline muted loop autoplay></video>
</template>

<script setup>
// 更改：导入 computed
import { ref, onMounted, onBeforeUnmount, watch, computed } from 'vue'

const props = defineProps({
  src: {
    type: [String, Array], // 支持字符串或数组格式
    required: true,
  },
  isOrigin: {
    type: Boolean,
    default: false,
  },
  videoAttrs: {
    type: Object,
    default: () => ({
      width: '640',
      height: '360',
    }),
  },
  // 新增：封面图片 prop
  coverImage: {
    type: String,
    default: '/images/mobile/poster-time.png',
  }
})

// 新增：computed 属性来合并 videoAttrs 和 poster
const computedVideoAttrs = computed(() => {
  return {
    ...props.videoAttrs,
  }
})

const videoPlayer = ref(null)
let hls = null
const isMuted = ref(true)
let currentSourceIndex = 0 // 当前播放源索引
let retryCount = 0 // 重试次数
const maxRetries = 3 // 最大重试次数

// 处理视频源格式的函数
const processVideoSources = (sources) => {
  // 如果是数组格式，直接返回
  if (Array.isArray(sources)) {
    return sources;
  }
  // 如果是字符串格式，转换为数组格式
  if (typeof sources === 'string') {
    return [{
      src: sources,
      type: sources.endsWith('.mp4') ? 'video/mp4' :
        sources.endsWith('.webm') ? 'video/webm' :
          'video/mp4'
    }];
  }
  // 默认返回空数组
  return [];
}

const isHttpsUrl = (path) => path.startsWith('https://')

// 初始化播放器
const initializePlayer = () => {
  const videoElement = videoPlayer.value
  if (!videoElement) return

  // 处理视频源
  const videoSources = processVideoSources(props.src);
  const currentSource = videoSources[currentSourceIndex] || videoSources[0];

  if (props.isOrigin) {
    videoElement.src = currentSource.src
    attachEventListeners(videoElement)
    return
  }

  // 为原生视频元素添加事件监听器
  attachEventListeners(videoElement)

  // 设置视频源
  videoElement.src = currentSource.src
}

// 为视频元素添加事件监听器
const attachEventListeners = (videoElement) => {
  // 移除之前的事件监听器（如果有的话）
  videoElement.removeEventListener('error', handleVideoError)
  videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata)

  // 添加新的事件监听器
  videoElement.addEventListener('error', handleVideoError)
  videoElement.addEventListener('loadedmetadata', handleLoadedMetadata)

  // 尝试播放
  videoElement.play().catch(() => {
    console.log('自动播放失败')
  })
}

// 处理视频加载元数据事件
const handleLoadedMetadata = (e) => {
  console.log('视频元数据加载完成')
  // 重置重试计数
  retryCount = 0
}

// 处理视频错误事件
const handleVideoError = (e) => {
  console.log('视频播放错误:', e)

  if (retryCount < maxRetries) {
    retryCount++
    console.log(`重试次数: ${retryCount}`)

    setTimeout(() => {
      switchToNextSource()
    }, 1500)
  } else {
    // 如果达到最大重试次数，尝试切换到 MP4 格式
    console.log('尝试切换到 MP4 格式')
    switchToMP4Source()
  }
}

// 切换到下一个视频源
const switchToNextSource = () => {
  const videoSources = processVideoSources(props.src)
  if (videoSources.length > 1) {
    currentSourceIndex = (currentSourceIndex + 1) % videoSources.length
    const nextSource = videoSources[currentSourceIndex]
    console.log(`切换到视频源: ${nextSource.src}`)

    const videoElement = videoPlayer.value
    if (videoElement) {
      videoElement.src = nextSource.src
      videoElement.load()
      videoElement.play().catch(() => {
        console.log('切换源后播放失败')
      })
    }
  }
}

// 切换到 MP4 视频源
const switchToMP4Source = () => {
  const domain = typeof window !== "undefined" ? window.location.origin : ""
  const mp4Source = domain + "/videos/hg-introduction-video.mp4"

  console.log(`切换到 MP4 视频源: ${mp4Source}`)

  const videoElement = videoPlayer.value
  if (videoElement) {
    videoElement.src = mp4Source
    videoElement.load()
    videoElement.play().catch(() => {
      console.log('切换到 MP4 源后播放失败')
    })
  }
}

onMounted(() => {
  initializePlayer()
})

onBeforeUnmount(() => {
  if (hls) hls.destroy()
})

watch(() => props.src, () => {
  // 重置索引和重试计数
  currentSourceIndex = 0
  retryCount = 0
  initializePlayer()
})
</script>

<style scoped>
.video-js {
  width: 100% !important;
  height: 100% !important;

  /* 注意: object-fit: fill 可能会拉伸你的封面图片。
    如果你希望封面图片保持比例并裁剪，可以考虑使用 object-fit: cover;
    或者通过 ::v-deep 伪类选择器单独为 poster 设置样式。
    但 video.js 的 poster 通常是作为背景图片应用的，
    video.js 默认会给 .vjs-poster 元素设置 background-size: cover;
    你这里似乎没有用 video.js 的 JS 库，只是用了它的 CSS，
    所以原生的 poster 行为会生效，它会和 object-fit: fill; 一起工作。
    如果 poster 被拉伸了，你可能需要调整样式。
  */
  object-fit: fill;
}

/* 切换动效 */
.mute-toggle-enter-active,
.mute-toggle-leave-active {
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.mute-toggle-enter-from,
.mute-toggle-leave-to {
  opacity: 0;
  transform: scale(0.8) rotate(-10deg);
}

.mute-toggle-enter-to,
.mute-toggle-leave-from {
  opacity: 1;
  transform: scale(1) rotate(0);
}
</style>
